// SocialPulse OIDC helpers — public-client PKCE flow.
// The frontend never sees SocialPulse tokens; it only obtains the authorization
// `code` + hands it to our backend, which does the server-to-server exchange.
//
// CRA env vars only (no Vite):
//   REACT_APP_SOCIALPULSE_OIDC_ISSUER    e.g. "https://socialpulse.com/api"
//   REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID e.g. "naturepulse-web"

const ISSUER = process.env.REACT_APP_SOCIALPULSE_OIDC_ISSUER || "";
const CLIENT_ID = process.env.REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID || "naturepulse-web";
const STORAGE_KEY = "np:oidc:pkce";

// Ecosystem contract: production callback is https://<domain>/auth/callback.
// Preview origins keep the previously whitelisted /auth/socialpulse-callback
// until SpLink admin whitelists the contract path for preview hosts too.
const CALLBACK_PATH = (() => {
  if (typeof window !== "undefined" && /preview\.emergentagent\.com$/.test(window.location.hostname)) {
    return "/auth/socialpulse-callback";
  }
  return process.env.REACT_APP_SOCIALPULSE_CALLBACK_PATH || "/auth/callback";
})();

export const socialPulseEnabled = () => !!ISSUER;

const b64u = (buf) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const rand = () => b64u(crypto.getRandomValues(new Uint8Array(32)).buffer);

export async function startSocialPulseLogin({ returnTo = "/" } = {}) {
  if (!ISSUER) throw new Error("SocialPulse SSO not configured");
  const verifier = rand();
  const state = rand();
  const nonce = rand();
  const challenge = b64u(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))
  );
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ verifier, state, nonce, returnTo }));
  const q = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
    scope: "openid profile email",
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.assign(`${ISSUER}/oauth/authorize?${q.toString()}`);
}

// Called by the callback page. `http` is our axios instance with baseURL=`${BACKEND}/api`.
export async function finishSocialPulseLogin(http) {
  const p = new URLSearchParams(window.location.search);
  const err = p.get("error");
  if (err) throw new Error(p.get("error_description") || err);
  const code = p.get("code");
  const state = p.get("state");
  if (!code || !state) throw new Error("missing code or state");
  const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
  if (!saved || saved.state !== state) throw new Error("state mismatch (CSRF)");

  const { data } = await http.post("/auth/socialpulse/session", {
    code,
    code_verifier: saved.verifier,
    nonce: saved.nonce,
    redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
  });
  sessionStorage.removeItem(STORAGE_KEY);
  return { ...data, returnTo: saved.returnTo || "/" };
}
